import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiX, FiUser, FiLock, FiShield, FiLogIn } from 'react-icons/fi';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import toast from 'react-hot-toast';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  min-height: 100vh;
  min-height: 100dvh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  box-sizing: border-box;
`;

const Modal = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a3e 0%, #2d2d5f 100%);
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  max-height: 90dvh;
  overflow: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  flex-shrink: 0;
  margin: auto;
`;

const Header = styled.div`
  position: relative;
  padding: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;

  .logo {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: #fff;
    margin: 0 auto 1rem;
  }

  h2 {
    color: #fff;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
  }

  .subtitle {
    color: #a0a0a0;
    font-size: 0.9rem;
  }

  .close-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 8px;
    padding: 0.5rem;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const Form = styled.form`
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    color: #fff;
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  .input-container {
    position: relative;
  }

  input {
    width: 100%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: #4facfe;
      box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  .input-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #a0a0a0;
    pointer-events: none;
  }

  .field-error {
    color: #ff6b6b;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ForgotPassword = styled.button`
  background: none;
  border: none;
  color: #4facfe;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
  margin: 0 auto;
  display: block;

  &:hover {
    color: #00f2fe;
  }
`;

const IssuerLogin = ({ isOpen, onClose, onLogin }) => {
  const { loginIssuer } = useTokenIssuer();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const issuer = await loginIssuer(formData);
      
      toast.success(`Welcome back, ${issuer.name}!`);
      
      // Call parent login handler
      if (onLogin) {
        onLogin(issuer);
      }
      
      onClose();
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    toast.success('Password reset instructions sent to your email');
  };

  if (!isOpen) return null;

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Modal
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <Header>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
          
          <div className="logo">
            <FiShield />
          </div>
          
          <h2>Core Tokenizer Login</h2>
          <div className="subtitle">
            Access your Core tokenizer dashboard
          </div>
        </Header>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Email Address</label>
            <div className="input-container">
              <FiUser className="input-icon" />
              <input
                type="email"
                placeholder="Enter your registered email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </div>
            {errors.email && (
              <div className="field-error">{errors.email}</div>
            )}
          </FormGroup>

          <FormGroup>
            <label>Password</label>
            <div className="input-container">
              <FiLock className="input-icon" />
              <input
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
              />
            </div>
            {errors.password && (
              <div className="field-error">{errors.password}</div>
            )}
          </FormGroup>

          <LoginButton type="submit" disabled={isLoading}>
            {isLoading ? 'Signing In...' : 'Sign In'}
            <FiLogIn />
          </LoginButton>

          <ForgotPassword type="button" onClick={handleForgotPassword}>
            Forgot your password?
          </ForgotPassword>
        </Form>
      </Modal>
    </Overlay>
  );
};

export default IssuerLogin; 